import { Injectable, signal, effect, DOCUMENT } from '@angular/core';

import { inject } from '@angular/core';

export type Theme = 'light' | 'dark' | 'auto';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private document = inject(DOCUMENT);

  private readonly storageKey = 'app-theme';
  private readonly _currentTheme = signal<Theme>(this.getInitialTheme());

  readonly currentTheme = this._currentTheme.asReadonly();

  // For backward compatibility
  readonly isDarkTheme = signal<boolean>(false);

  constructor() {
    effect(() => {
      this.applyTheme(this._currentTheme());
      // Update the legacy isDarkTheme signal
      this.isDarkTheme.set(this.getEffectiveTheme() === 'dark');
    });
  }

  private getInitialTheme(): Theme {
    const stored = localStorage.getItem(this.storageKey);
    if (stored === 'true' || stored === 'false') {
      // Migrate from old boolean storage
      return stored === 'true' ? 'dark' : 'light';
    }
    return (stored as Theme) || 'light';
  }

  setTheme(theme: Theme): void {
    this._currentTheme.set(theme);
    localStorage.setItem(this.storageKey, theme);
  }

  toggleTheme(): void {
    const current = this._currentTheme();
    if (current === 'auto') {
      const effective = this.getEffectiveTheme();
      this.setTheme(effective === 'dark' ? 'light' : 'dark');
    } else {
      this.setTheme(current === 'light' ? 'dark' : 'light');
    }
  }

  private applyTheme(theme: Theme): void {
    const html = this.document.documentElement;

    // Remove existing theme classes
    html.classList.remove('light', 'dark');

    if (theme === 'auto') {
      // Let CSS media queries handle auto theme
      return;
    }

    // Apply specific theme class
    html.classList.add(theme);
  }

  getEffectiveTheme(): 'light' | 'dark' {
    const theme = this._currentTheme();

    if (theme === 'auto') {
      // Check system preference
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }

    return theme;
  }
}
